import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';

export type RisingCreatorStatus = 'new' | 'shortlisted' | 'contacted' | 'in_discussion' | 'signed' | 'rejected';
export type RisingCreatorPlatform = 'instagram' | 'tiktok';
export type RisingCreatorSource = 'hashtag' | 'follow_graph' | 'tiktok_rising' | 'sponsored_collab';

export interface RisingCreator {
  id: string;
  username: string;
  platform: RisingCreatorPlatform;
  full_name: string | null;
  profile_url: string | null;
  profile_picture_url: string | null;
  bio: string | null;
  email: string | null;
  followers_count: number;
  following_count: number | null;
  posts_count: number | null;
  engagement_rate: number | null;
  avg_views_reels: number | null;
  growth_rate_30d: number | null;
  social_proof_count: number;
  source: RisingCreatorSource;
  source_details: Record<string, any> | null;
  rising_score: number;
  status: RisingCreatorStatus;
  assigned_to: string | null;
  notes: string | null;
  first_seen_at: string;
  last_updated_at: string;
  last_scraped_at: string | null;
}

export interface RisingCreatorSnapshot {
  id: string;
  rising_creator_id: string;
  followers_count: number | null;
  engagement_rate: number | null;
  snapshot_at: string;
}

export interface RisingCreatorsFilters {
  platform: RisingCreatorPlatform | 'all';
  source: RisingCreatorSource | 'all';
  minFollowers: number;
  maxFollowers: number;
  minScore: number;
  statuses: RisingCreatorStatus[];
  sortBy: 'score' | 'recent' | 'growth';
  search: string;
}

export const DEFAULT_FILTERS: RisingCreatorsFilters = {
  platform: 'all',
  source: 'all',
  minFollowers: 5000,
  maxFollowers: 80000,
  minScore: 0,
  statuses: [],
  sortBy: 'score',
  search: '',
};

export function useRisingCreators() {
  const [creators, setCreators] = useState<RisingCreator[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<RisingCreatorsFilters>(DEFAULT_FILTERS);
  const [page, setPage] = useState(0);
  const PAGE_SIZE = 50;

  const loadCreators = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      let query = supabase
        .from('rising_creators')
        .select('*');

      if (filters.platform !== 'all') {
        query = query.eq('platform', filters.platform);
      }
      if (filters.source !== 'all') {
        query = query.eq('source', filters.source);
      }
      if (filters.statuses.length > 0) {
        query = query.in('status', filters.statuses);
      }
      if (filters.minFollowers > 0) {
        query = query.gte('followers_count', filters.minFollowers);
      }
      if (filters.maxFollowers < 200000) {
        query = query.lte('followers_count', filters.maxFollowers);
      }
      if (filters.minScore > 0) {
        query = query.gte('rising_score', filters.minScore);
      }
      if (filters.search) {
        query = query.or(`username.ilike.%${filters.search}%,full_name.ilike.%${filters.search}%`);
      }

      if (filters.sortBy === 'score') {
        query = query.order('rising_score', { ascending: false });
      } else if (filters.sortBy === 'recent') {
        query = query.order('first_seen_at', { ascending: false });
      } else if (filters.sortBy === 'growth') {
        query = query.order('growth_rate_30d', { ascending: false });
      }

      query = query.range(page * PAGE_SIZE, (page + 1) * PAGE_SIZE - 1);

      const { data, error: err } = await query;
      if (err) throw err;
      setCreators(data || []);
    } catch (err: any) {
      setError(err.message || 'Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  }, [filters, page]);

  useEffect(() => {
    loadCreators();
  }, [loadCreators]);

  const updateStatus = async (id: string, status: RisingCreatorStatus) => {
    const { error } = await supabase
      .from('rising_creators')
      .update({ status, last_updated_at: new Date().toISOString() })
      .eq('id', id);
    if (!error) {
      setCreators(prev => prev.map(c => c.id === id ? { ...c, status } : c));
    }
    return !error;
  };

  const updateNotes = async (id: string, notes: string) => {
    const { error } = await supabase
      .from('rising_creators')
      .update({ notes, last_updated_at: new Date().toISOString() })
      .eq('id', id);
    if (!error) {
      setCreators(prev => prev.map(c => c.id === id ? { ...c, notes } : c));
    }
    return !error;
  };

  const updateAssignedTo = async (id: string, assigned_to: string) => {
    const { error } = await supabase
      .from('rising_creators')
      .update({ assigned_to, last_updated_at: new Date().toISOString() })
      .eq('id', id);
    if (!error) {
      setCreators(prev => prev.map(c => c.id === id ? { ...c, assigned_to } : c));
    }
    return !error;
  };

  const bulkUpdateStatus = async (ids: string[], status: RisingCreatorStatus) => {
    const { error } = await supabase
      .from('rising_creators')
      .update({ status, last_updated_at: new Date().toISOString() })
      .in('id', ids);
    if (!error) {
      setCreators(prev => prev.map(c => ids.includes(c.id) ? { ...c, status } : c));
    }
    return !error;
  };

  const bulkAssign = async (ids: string[], assigned_to: string) => {
    const { error } = await supabase
      .from('rising_creators')
      .update({ assigned_to, last_updated_at: new Date().toISOString() })
      .in('id', ids);
    if (!error) {
      setCreators(prev => prev.map(c => ids.includes(c.id) ? { ...c, assigned_to } : c));
    }
    return !error;
  };

  const loadSnapshots = async (creatorId: string): Promise<RisingCreatorSnapshot[]> => {
    const { data } = await supabase
      .from('rising_creators_snapshots')
      .select('*')
      .eq('rising_creator_id', creatorId)
      .order('snapshot_at', { ascending: true });
    return data || [];
  };

  const convertToRecruitment = async (creator: RisingCreator): Promise<boolean> => {
    const { error } = await supabase
      .from('recruitment_influencers')
      .insert({
        username: creator.username,
        full_name: creator.full_name || creator.username,
        email: creator.email,
        profile_picture: creator.profile_picture_url,
        instagram_url: creator.platform === 'instagram' ? creator.profile_url : null,
        tiktok_url: creator.platform === 'tiktok' ? creator.profile_url : null,
        notes: creator.notes,
        status: 'spotted',
      });
    return !error;
  };

  const getStats = useCallback(() => {
    const now = new Date();
    const oneWeekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
    const oneMonthAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);

    const newThisWeek = creators.filter(
      c => new Date(c.first_seen_at) >= oneWeekAgo && c.status === 'new'
    ).length;
    const inDiscussion = creators.filter(c => c.status === 'in_discussion').length;
    const signedThisMonth = creators.filter(
      c => c.status === 'signed' && new Date(c.last_updated_at) >= oneMonthAgo
    ).length;
    const top20 = [...creators]
      .sort((a, b) => b.rising_score - a.rising_score)
      .slice(0, 20);
    const avgScoreTop20 =
      top20.length > 0
        ? Math.round(top20.reduce((sum, c) => sum + c.rising_score, 0) / top20.length)
        : 0;

    return { newThisWeek, inDiscussion, signedThisMonth, avgScoreTop20 };
  }, [creators]);

  return {
    creators,
    loading,
    error,
    filters,
    setFilters,
    page,
    setPage,
    reload: loadCreators,
    updateStatus,
    updateNotes,
    updateAssignedTo,
    bulkUpdateStatus,
    bulkAssign,
    loadSnapshots,
    convertToRecruitment,
    getStats,
    PAGE_SIZE,
  };
}
